'use client';

import {
    Button,
    Chip,
    Input,
    Popover,
    PopoverContent,
    PopoverTrigger,
    Select,
    SelectItem,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import type { DataInputFieldProps } from './props';

export function DataInputField({
    input,
    value,
    isReadOnly,
    addTagField,
    newTagValue,
    onSetAddTagField,
    onSetNewTagValue,
    onRemoveTag,
    onAddTag,
    onDropdownChange,
}: DataInputFieldProps) {
    switch (input.type) {
        case 'multi_tag': {
            const tags = Array.from(
                new Set(
                    (Array.isArray(value) ? value : [value])
                        .map((v) => String(v).trim())
                        .filter(Boolean),
                ),
            );
            return (
                <div>
                    <p className="mb-2 text-small font-medium text-default-600">{input.label}</p>
                    <div className="flex flex-wrap items-center gap-1.5">
                        {tags.map((tag) => (
                            <Chip
                                key={tag}
                                variant="flat"
                                size="sm"
                                classNames={{
                                    base: 'bg-default-100 text-default-700 transition-colors hover:bg-default-200',
                                    closeButton: 'text-default-400 hover:text-default-600',
                                }}
                                onClose={
                                    !isReadOnly ? () => onRemoveTag(input.key, tag) : undefined
                                }
                            >
                                {tag}
                            </Chip>
                        ))}
                        {!isReadOnly && (input.allow_add ?? true) && (
                            <Popover
                                isOpen={addTagField === input.key}
                                onOpenChange={(open) => {
                                    onSetAddTagField(open ? input.key : null);
                                    if (!open) onSetNewTagValue('');
                                }}
                                placement="bottom"
                            >
                                <PopoverTrigger>
                                    <Button
                                        size="sm"
                                        variant="flat"
                                        className="h-6 min-w-0 gap-1 rounded-full bg-default-100 px-2.5 text-tiny font-medium text-default-500 data-[hover=true]:bg-default-200"
                                    >
                                        <Icon icon="lucide:plus" width={12} />
                                        Add
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="p-2">
                                    <Input
                                        size="sm"
                                        placeholder={`Add ${input.label.toLowerCase()}`}
                                        value={newTagValue}
                                        onValueChange={onSetNewTagValue}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                onAddTag(input.key, newTagValue);
                                            }
                                        }}
                                        endContent={
                                            <Button
                                                size="sm"
                                                isIconOnly
                                                variant="light"
                                                aria-label={`Add ${input.label.toLowerCase()}`}
                                                onPress={() => onAddTag(input.key, newTagValue)}
                                            >
                                                <Icon icon="lucide:plus" width={14} />
                                            </Button>
                                        }
                                        autoFocus
                                    />
                                </PopoverContent>
                            </Popover>
                        )}
                    </div>
                </div>
            );
        }

        case 'dropdown': {
            const currentVal = typeof value === 'string' ? value : '';
            return (
                <div>
                    <p className="mb-2 text-small font-medium text-default-600">{input.label}</p>
                    <Select
                        size="sm"
                        className="max-w-[220px]"
                        selectedKeys={currentVal ? new Set([currentVal]) : new Set()}
                        onSelectionChange={(keys) => {
                            if (keys === 'all' || keys.size === 0) return;
                            const selected = Array.from(keys)[0] as string;
                            if (selected) onDropdownChange(input.key, selected);
                        }}
                        isDisabled={isReadOnly}
                        aria-label={input.label}
                        classNames={{
                            trigger: 'border-default-200 data-[hover=true]:border-default-300',
                        }}
                    >
                        {(input.options || []).map((opt) => (
                            <SelectItem key={opt.value}>{opt.label}</SelectItem>
                        ))}
                    </Select>
                </div>
            );
        }

        default:
            return null;
    }
}
